/**
 * Frontend Logger for Chatealo MiniApp
 *
 * Provides consistent logging with levels and context.
 * Only logs in development mode to avoid polluting production console.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  restaurantId?: string;
  orderId?: string;
  component?: string;
  action?: string;
  [key: string]: unknown;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Check if we're in development mode
const isDev = import.meta.env.DEV;

// In production, only log errors. In development, log everything.
const MIN_LOG_LEVEL: LogLevel = isDev ? 'debug' : 'error';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LOG_LEVEL];
}

function formatMessage(level: LogLevel, message: string, context?: LogContext): string {
  const emoji = {
    debug: '🔍',
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
  }[level];

  let output = `${emoji} [${level.toUpperCase()}] ${message}`;

  if (context && Object.keys(context).length > 0) {
    output += ` | ${JSON.stringify(context)}`;
  }

  return output;
}

function log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
  if (!shouldLog(level)) return;

  const formatted = formatMessage(level, message, context);

  switch (level) {
    case 'debug':
      console.debug(formatted);
      break;
    case 'info':
      console.info(formatted);
      break;
    case 'warn':
      console.warn(formatted);
      break;
    case 'error':
      if (error) {
        console.error(formatted, error);
      } else {
        console.error(formatted);
      }
      break;
  }
}

/**
 * Logger with scoped context
 * Usage:
 *   const log = logger.withContext({ component: 'Cart' });
 *   log.info('Item added', { itemId: 'item_123' });
 */
export const logger = {
  debug: (message: string, context?: LogContext) => log('debug', message, context),
  info: (message: string, context?: LogContext) => log('info', message, context),
  warn: (message: string, context?: LogContext) => log('warn', message, context),
  error: (message: string, context?: LogContext, error?: Error) =>
    log('error', message, context, error),

  /**
   * Create a logger with pre-set context
   */
  withContext: (baseContext: LogContext) => ({
    debug: (message: string, context?: LogContext) =>
      log('debug', message, { ...baseContext, ...context }),
    info: (message: string, context?: LogContext) =>
      log('info', message, { ...baseContext, ...context }),
    warn: (message: string, context?: LogContext) =>
      log('warn', message, { ...baseContext, ...context }),
    error: (message: string, context?: LogContext, error?: Error) =>
      log('error', message, { ...baseContext, ...context }, error),
  }),
};

export type Logger = typeof logger;
export type ScopedLogger = ReturnType<typeof logger.withContext>;

